import { useState, useMemo } from "react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { AssignmentDialogShell, AssignOptionRow } from "./AssignmentDialogShell";

/**
 * ProductSizingImageDialog — attaches a DAM library image to the product.
 * Shows the tenant's uploaded sizing-chart images (thumbnails + names).
 * Searchable when the catalog gets big.
 */
export const ProductSizingImageDialog = ({ product, onClose }) => {
    const { images, setProductSizingImage } = useSizing();
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);
    const currentId = product?.sizingImageId || null;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return images;
        return images.filter((img) => (img.name || "").toLowerCase().includes(q));
    }, [images, query]);

    const apply = async (imgId) => {
        if (saving) return;
        setSaving(true);
        try {
            await setProductSizingImage(product.id, imgId);
            toast.success(imgId ? "Görsel bu ürüne bağlandı" : "Görsel bağlantısı kaldırıldı");
            onClose();
        } catch {
            toast.error("Görsel güncellenemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AssignmentDialogShell
            title="Beden tablosu görseli"
            product={product}
            onClose={onClose}
            searchable
            searchValue={query}
            onSearchChange={setQuery}
            searchPlaceholder="Görsel ara…"
            onClear={currentId ? () => apply(null) : null}
            testId="product-sizing-image-dialog"
        >
            {images.length === 0 ? (
                <div className="text-center py-10 text-xs text-muted-foreground">
                    Henüz görsel yok.{" "}
                    <a href="/app/sizing/images" target="_blank" rel="noopener" className="underline hover:text-foreground">
                        Görseller sayfasından
                    </a>{" "}
                    yükle.
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-xs text-muted-foreground">Sonuç yok</div>
            ) : (
                filtered.map((img) => (
                    <AssignOptionRow
                        key={img.id}
                        current={img.id === currentId}
                        onClick={() => apply(img.id)}
                        disabled={saving}
                        testId={`sizing-image-option-${img.id}`}
                    >
                        <div className="h-10 w-10 rounded-md border border-border bg-muted/40 overflow-hidden shrink-0 p-1">
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/embed/images/${img.id}`}
                                alt=""
                                className="block w-full h-full object-contain"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{img.name || "İsimsiz görsel"}</div>
                            {img.alt && (
                                <div className="text-[10.5px] text-muted-foreground truncate">{img.alt}</div>
                            )}
                        </div>
                    </AssignOptionRow>
                ))
            )}
        </AssignmentDialogShell>
    );
};
